import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { PartnerMembershipService } from '@sisoma/auth';

import { NoRole } from './no-role';
import { Suspended } from './suspended';

/**
 * Stands where the shell's `<router-outlet />` used to, and decides whether the
 * app renders at all for this person in this partner area.
 *
 * **It owns the outlet rather than projecting it.** An `<ng-content />` inside
 * an `@if` would look equivalent and would not be: projected content is
 * instantiated by the component that *declares* it, so a projected outlet would
 * activate the routed component and fire its requests for a blocked user, out
 * of sight. Owning the outlet is what makes the blocked branch actually lazy.
 *
 * Chosen over the two routing-based alternatives because the URL has to
 * survive. A dedicated `/no-role` route would sit outside the shell — the house
 * pattern for `/forbidden` — which loses the sidenav, and with it the partner
 * switcher that a multi-partner user needs to get out of the area they have no
 * role in. It would also turn a bookmark into `/no-role` and risk the redirect
 * loop the route table warns about. Here the URL never changes, so being given
 * a role and reloading lands on the page originally asked for. `blockedBy` is a
 * signal, so the app also returns the moment the state changes, with no
 * re-navigation.
 *
 * The `resolving` branch exists because "not blocked" is the safe default while
 * the membership request is in flight, and rendering the outlet on that default
 * would flash the dashboard at someone who is about to be told they have no
 * role. It renders nothing visible; the notice fades in only if the request is
 * slow enough to be worth acknowledging.
 */
@Component({
  selector: 'app-membership-gate',
  imports: [RouterOutlet, NoRole, Suspended],
  templateUrl: './membership-gate.html',
  styleUrl: './membership-gate.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipGate {
  private readonly membership = inject(PartnerMembershipService);

  protected readonly resolving = this.membership.resolving;
  protected readonly blockedBy = this.membership.blockedBy;
}
